import moment from "moment";
import { TIME_FORMAT_H24 } from "types";

export interface BufferTimeCalculationResult {
  startTimeWithBuffer: moment.Moment;
  endTimeWithBuffer: moment.Moment;
}

export interface BufferTimeOptions {
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  timezone?: string;
}

/**
 * Calculate start and end times with buffer minutes applied
 * @param startTime - The original start time (moment object or string)
 * @param endTime - The original end time (moment object or string)
 * @param options - Buffer time options
 * @returns Object containing buffered start and end times
 */
export const calculateBufferTimes = (
  startTime: moment.Moment | string,
  endTime: moment.Moment | string,
  options: BufferTimeOptions = {},
): BufferTimeCalculationResult => {
  const { bufferBeforeMinutes = 0, bufferAfterMinutes = 0, timezone } = options;

  // Convert to moment objects if needed
  let startMoment = moment.isMoment(startTime) ? startTime.clone() : moment(startTime);
  let endMoment = moment.isMoment(endTime) ? endTime.clone() : moment(endTime);

  // Apply timezone if provided
  if (timezone) {
    startMoment = startMoment.tz(timezone);
    endMoment = endMoment.tz(timezone);
  }

  // Calculate buffered times
  const startTimeWithBuffer = startMoment.subtract(bufferBeforeMinutes || 0, "minutes");
  const endTimeWithBuffer = endMoment.add(bufferAfterMinutes || 0, "minutes");

  return {
    startTimeWithBuffer,
    endTimeWithBuffer,
  };
};

/**
 * Calculate buffer times for form validation (without timezone)
 * @param startTime - The original start time string (HH:mm format)
 * @param endTime - The original end time string (HH:mm format)
 * @param bufferBeforeMinutes - Buffer before in minutes
 * @param bufferAfterMinutes - Buffer after in minutes
 * @returns Object containing buffered start and end times
 */
export const calculateBufferTimesForValidation = (
  startTime: string,
  endTime: string,
  bufferBeforeMinutes?: number | null,
  bufferAfterMinutes?: number | null,
): BufferTimeCalculationResult => {
  const start = moment(startTime, TIME_FORMAT_H24);
  const end = moment(endTime, TIME_FORMAT_H24);

  return calculateBufferTimes(start, end, {
    bufferBeforeMinutes,
    bufferAfterMinutes,
  });
};
